import BigNumber from 'bignumber.js';
import Service, { serviceSelector, useService } from 'react-reservice';

import { EInputType } from '@webtrader/core/chart/events/indicators';
import { numberOrDefault, numberOrUndefined } from '@webtrader/core/format/number';

export const valueToStringElseUndefined = (value: number | boolean | null | undefined) => {
  return value === null || value === undefined ? undefined : String(value);
};

export const getMappedEnumLabel = <T extends string | number>(
  translationMap: Record<T, string>,
  valueToTranslate: T | null = null
) => {
  return valueToTranslate === null ? '-' : translationMap[valueToTranslate] || String(valueToTranslate);
};

export const getMappedEnumLabelFn = <T extends string | number>(
  translationMap: Record<T, () => string>,
  valueToTranslate: T | null = null
) => {
  return valueToTranslate === null ? () => '-' : translationMap[valueToTranslate] || (() => String(valueToTranslate));
};

interface IServiceContext {}

export const getUseServiceWithSubscriptionKeys = <T extends IServiceContext>(service: Service<T>) => (
  subscriptionKeys: (keyof T)[]
) => useService(service, serviceSelector<typeof service, T>(subscriptionKeys));

interface IDestructureAsReferenceBase {}

/* Keeping the below code as another example of typing without Record<> */
// export const destructureAsNumber = <T extends IDestructureAsReferenceBase, K extends keyof T>(
//   referenceObj: T | undefined,
//   keys: K[]
// ): { [Key in K]: number } =>
//   keys.reduce(
//     (acc, key) => ({
//       ...acc,
//       [key]: numberOrDefault(referenceObj?.[key], 0)
//     }),
//     {} as { [Key in K]: number }
//   );

export const destructureAsNumber = <T extends IDestructureAsReferenceBase, K extends keyof T>(
  referenceObj: T | undefined,
  keys: K[]
): Record<K, number> =>
  keys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: numberOrDefault(referenceObj?.[key], 0)
    }),
    {} as Record<K, number>
  );

export const destructureAsNumberOrUndefined = <T extends IDestructureAsReferenceBase, K extends keyof T>(
  referenceObj: T | undefined,
  keys: K[]
): Record<K, number | undefined> =>
  keys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: numberOrUndefined(referenceObj?.[key])
    }),
    {} as Record<K, number | undefined>
  );

export const destructureAsBigNumber = <T extends IDestructureAsReferenceBase, K extends keyof T>(
  referenceObj: T | undefined,
  keys: K[]
): Record<K, BigNumber> =>
  keys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: new BigNumber(numberOrDefault(referenceObj?.[key], 0))
    }),
    {} as Record<K, BigNumber>
  );

export const destructureObject = <T extends IDestructureAsReferenceBase, K extends keyof T>(
  referenceObj: T | undefined,
  keys: K[]
): Record<K, T[K]> =>
  keys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: referenceObj?.[key]
    }),
    {} as Record<K, T[K]>
  );

/**
 * Validates numeric values with optional negative sign and customizable decimal precision.
 *
 * Acceptable Values:
 * - Without options (default behavior):
 *   - 123
 *   - 1.23
 *   - -123
 *   - -1.23
 *
 * - With `allowNegative: true`:
 *   - -123
 *   - -1.23
 *
 * - With `maxDecimals: 0`:
 *   - 123
 *
 * - With `maxDecimals: 2`:
 *   - 123.45
 *   - -1.23
 *
 * - With both `allowNegative: true` and `maxDecimals: 3`:
 *   - -123.456
 *   - -1.234
 *   - 123.456
 *
 * Remarks:
 *  - This regex allowed values like 0. or .0 or .
 *  - Please handle it separately if your case doesn't accept above value.
 *
 * @param {Object} option - Options for validation.
 *   @param {boolean} option.allowNegative - (Optional) Allow negative values. Default is false.
 *   @param {number} option.maxDecimals - (Optional) Maximum number of decimal places. Default is unlimited. Pass 0 if don't want to accept decimal value.
 *   @param {number} option.maxDigits - (Optional) Maximum total digits (excluding commas and decimal point). Default is unlimited.
 * @returns {IValidationRegex} Object with test method for validating numeric values.
 */
export interface IValidationRegex {
  test: (value: string) => boolean;
}

export const numericRegex = (option?: {
  allowNegative?: boolean;
  maxDecimals?: number;
  maxDigits?: number;
}): IValidationRegex => {
  const { allowNegative, maxDecimals, maxDigits } = option || {};
  let decimalsPart: string;

  if (maxDecimals === undefined) {
    decimalsPart = `*`;
  } else if (maxDecimals === 0) {
    decimalsPart = '';
  } else {
    decimalsPart = `{0,${maxDecimals}}`;
  }

  const baseRegex = `^${allowNegative ? '-?' : ''}\\d*?${decimalsPart ? `\\.?\\d${decimalsPart}` : ''}$`;

  return {
    test: (value: string) => {
      const stripped = value.replace(/,/g, '');
      if (!new RegExp(baseRegex).test(stripped)) {
        return false;
      }
      if (maxDigits !== undefined) {
        const digitCount = stripped.replace(/[^0-9]/g, '').length;
        return digitCount <= maxDigits;
      }
      return true;
    }
  };
};

export const mapZeroAndInvalidNumberToZero = (
  value: string,
  type: EInputType,
  onChange?: (value?: string | number) => void
) => {
  let inputValue = typeof value === 'number' ? value : (value ?? '').replace(/,/g, '');

  if (type === EInputType.NUMBER || type === EInputType.DECIMAL) {
    if (parseFloat(inputValue) === 0 || Number.isNaN(parseFloat(inputValue))) {
      inputValue = '1';
    } else if (inputValue.startsWith('.')) {
      inputValue = '0' + inputValue;
    } else if (inputValue.endsWith('.')) {
      inputValue = inputValue + '0';
    }
  }

  onChange?.(inputValue);

  return inputValue;
};
